import { Heart } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface BookmarkButtonProps {
  targetType: 'tenant' | 'exam';
  targetId: string;
  tenantId?: string; // Optional context
  initialIsBookmarked?: boolean;
  bookmarkId?: string; // If known
  className?: string;
  onToggle?: (isBookmarked: boolean) => void;
}

export function BookmarkButton({
  targetType,
  targetId,
  tenantId,
  initialIsBookmarked = false,
  bookmarkId: initialBookmarkId,
  className,
  onToggle,
}: BookmarkButtonProps) {
  const [isBookmarked, setIsBookmarked] = useState(initialIsBookmarked);
  const [bookmarkId, setBookmarkId] = useState<string | undefined>(initialBookmarkId);
  const [isLoading, setIsLoading] = useState(false);

  const handleToggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isLoading) return;

    setIsLoading(true);
    const newState = !isBookmarked;
    setIsBookmarked(newState); // Optimistic

    try {
      const authData = localStorage.getItem('admission-system-auth');
      const token = authData ? JSON.parse(authData).accessToken : null;

      if (!token) {
        console.error('No auth token found');
        setIsBookmarked(!newState); // Revert
        return;
      }

      if (newState) {
        // Create
        const res = await fetch('/api/bookmarks', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            targetType,
            targetId,
            tenantId,
          }),
        });
        const data = await res.json();
        if (data.success) {
          setBookmarkId(data.id);
          onToggle?.(true);
        } else {
          throw new Error('Failed to bookmark');
        }
      } else {
        // Delete
        if (!bookmarkId) {
          // Need to find bookmark Id if not provided?
          // For now, simplify: if we don't have ID, we can't delete easily via ID endpoint
          // UNLESS the API supported delete by target.
          // But our API is /bookmarks/:id.
          // Implementation Plan Check: "DELETE /bookmarks/:id"
          // If we optimistically toggled ON then OFF, we might not have ID yet if the first request didn't finish?
          // Wait, await finish above.
          // If initialIsBookmarked=true but no bookmarkId provided, we can't delete!
          // Ensure parent provides bookmarkId if known.
          // Or change API to allow delete by target?
          // Implementation choice: For now, assume ID is managed or fetched.
          // If missing, we might need to fetch list first.
          console.warn('Missing bookmarkId for deletion');
        } else {
          await fetch(`/api/bookmarks/${bookmarkId}`, {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          setBookmarkId(undefined);
          onToggle?.(false);
        }
      }
    } catch (error) {
      console.error('Bookmark error', error);
      setIsBookmarked(!newState); // Revert
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn(
        'hover:text-pink-500 hover:bg-pink-50 transition-colors',
        isBookmarked ? 'text-pink-600 fill-pink-600' : 'text-muted-foreground',
        className,
      )}
      onClick={handleToggle}
      disabled={isLoading}
    >
      <Heart className={cn('h-5 w-5', isBookmarked && 'fill-current')} strokeWidth={2} />
      <span className="sr-only">Bookmark</span>
    </Button>
  );
}
